/*
 * inselvolt Kompatibilitätsberater — Brücke zu Microsoft Clarity.
 *
 * Clarity wird hier NICHT geladen. Das Skript steht im Theme
 * (`layout/theme.liquid`, Projekt `lqpzntt8sj`) und hängt dort hinter der
 * Analyse-Einwilligung; siehe notes/tracking-ga4.md. Diese Datei sagt Clarity
 * nur, WAS auf der Seite gerade passiert — sie entscheidet nicht, OB gemessen
 * werden darf.
 *
 * Warum überhaupt eine zweite Meldestelle neben dem dataLayer?
 * Der dataLayer beantwortet „wie viele" (GA4, Google Ads). Clarity beantwortet
 * „wie" — Aufzeichnung, Heatmap, Wutklicks. Ohne diese Brücke weiß Clarity nur,
 * dass jemand auf der Seite war, nicht, ob er den Berater benutzt hat. Beides
 * speist sich aus DERSELBEN Quelle: den bestehenden emit()-Aufrufen in
 * app/berater-ui.js und app/berater-pdp.js. Es gibt bewusst keine zweite
 * Ereignisdefinition, die auseinanderlaufen könnte.
 *
 * Zwei Arten von Meldung, weil zwei Fragen:
 *
 *   Custom Event  clarity('event', name)      — ein einzelner Vorgang.
 *                 Beantwortet: Wie oft wird exportiert, geklickt, geöffnet?
 *
 *   Custom Tag    clarity('set', schlüssel, wert) — beschreibt die SITZUNG.
 *                 Beantwortet: Wie viele Sitzungen kamen bis zum Ergebnis?
 *                 Nur danach lässt sich im Clarity-Backend filtern und
 *                 segmentieren — deshalb liegt die Nutzungstiefe als Tag vor
 *                 und nicht nur als Ereignisfolge.
 *
 * EINWILLIGUNG. Vor dem Ja des Kunden gibt es kein `window.clarity`. Ereignisse
 * aus dieser Zeit werden VERWORFEN, nicht zwischengespeichert und nachgereicht:
 * Was vor der Einwilligung geschah, wird nicht gemessen. Die Tags dagegen
 * beschreiben den Zustand der laufenden Sitzung („dieser Besucher ist im
 * Berater, er ist bis zum Ergebnis gekommen"); sie werden nachgezogen, sobald
 * Clarity erscheint. Das datiert nichts zurück, sondern sagt über eine
 * einwilligende Sitzung, was gerade der Fall ist.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module !== 'undefined' && module.exports) module.exports = { erzeuge: factory, api: api };
  if (root && !root.IV_CLARITY) root.IV_CLARITY = api;
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  /*
   * Die Leiter der Nutzungstiefe — der eigentliche Ertrag dieser Datei.
   *
   * Ohne sie müsste man im Backend Ereignisse zählen und ins Verhältnis setzen.
   * Mit ihr ist „wie intensiv wird der Berater genutzt" ein Filter: Sitzungen
   * mit `iv_berater_tiefe = ergebnis` sind die, die eine Antwort bekommen haben;
   * `= lead` die, die daraufhin gehandelt haben.
   *
   * Gemeldet wird JEDE erreichte Stufe, nicht nur die höchste, und jede nur
   * einmal. Ein Rückschritt ist ausgeschlossen: Wer sortiert, nachdem er ein
   * Ergebnis gesehen hat, fällt nicht auf „ausgewaehlt" zurück.
   *
   * `null` heißt: gehört zu keiner Stufe. Das Ereignis wird trotzdem als Custom
   * Event gemeldet — es sagt nur nichts über die Tiefe aus (`preise_geladen` ist
   * Technik, `pdp_geschlossen` ein Rückschritt).
   *
   * Jeder Ereignisname aus berater-ui.js, berater-pdp.js und berater-einstieg.js MUSS hier stehen;
   * ein Test hält das fest. So fällt ein neues Ereignis ohne Einordnung auf,
   * statt still als tiefenlos durchzulaufen.
   */
  var KONTEXTE = {
    berater: {
      tiefe_schluessel: 'iv_berater_tiefe',
      leiter: ['geoeffnet', 'ausgewaehlt', 'ergebnis', 'vertieft', 'lead'],
      stufen: {
        geoeffnet: 'geoeffnet',
        aufgabe_gewaehlt: 'ausgewaehlt',
        wr_gewaehlt: 'ausgewaehlt',
        speicher_gewaehlt: 'ausgewaehlt',
        ergebnis_angezeigt: 'ergebnis',
        sortierung_gewaehlt: 'vertieft',
        leistungsfilter_gewaehlt: 'vertieft',
        detail_geoeffnet: 'vertieft',
        vergleich_geoeffnet: 'vertieft',
        /* Eine Anlagenfrage beantwortet (Notstrom, E-Auto, Wärmepumpe,
         * Wärmepumpen-Hersteller, Stromtarif, Hauptsicherung) — wer das tut,
         * liest das Ergebnis nicht nur. `iv_frage` trägt die Frage-ID; neue
         * Fragen brauchen deshalb keinen neuen Ereignisnamen. */
        anlagenfrage_gewaehlt: 'vertieft',
        /* Seit 2026-10-17 stehen die Fragen eingeklappt hinter dem
         * Ergebniskopf. Aufklappen ist Absicht, noch keine Angabe — als
         * Ereignis gezählt, ohne Sprosse (wie geraetesuche_geoeffnet). */
        anlagenfragen_geoeffnet: null,
        pdf_export: 'lead',
        lead_klick: 'lead',
        preise_geladen: null,
        /* Seit 2026-09-26: gewähltes Gerät hat keinen Batterieanschluss — der
         * Hinweis mit den Speicher-Wegen ist dort das Ergebnis. */
        netz_hinweis_angezeigt: 'ergebnis',
      },
      /*
       * Welche Felder als Tag mitgehen — bewusst eine Positivliste. Alles
       * andere bliebe entweder ohne Aussage (`iv_treffer` ist eine Zahl, nach
       * der niemand segmentiert) oder würde die Tag-Werte mit Einzelfällen
       * überschwemmen, bis der Filter unbrauchbar ist.
       */
      tags: ['iv_aufgabe', 'iv_ziel', 'iv_handle', 'iv_wr', 'iv_speicher'],
    },
    pdp: {
      tiefe_schluessel: 'iv_pdp_tiefe',
      leiter: ['teaser', 'geklickt', 'geoeffnet', 'geraet_genannt', 'urteil', 'vertieft', 'lead'],
      stufen: {
        /* Der Teaser steht auf der Seite, ohne dass jemand etwas tut — deshalb
         * ist er die Grundgesamtheit, gegen die alles andere zählt. */
        teaser_sichtbar: 'teaser',
        teaser_befund: 'teaser',
        /* Seit 2026-10-17: der Teaser war zur Hälfte im Bild (vorher gab es
         * nur die Anwesenheit). Ohne Sprosse — die Leiter bleibt
         * vergleichbar; die Quote je Sicht rechnet man über die Ereignisse. */
        teaser_gesehen: null,
        /* Die Freigabeliste im Teaser aufgeklappt — die Antwort an Ort und
         * Stelle gelesen, ohne Dialog. */
        freigaben_aufgeklappt: null,
        /*
         * Klick und geöffnetes Overlay sind ZWEI Stufen, obwohl sie meist
         * zusammenfallen: Zwischen ihnen liegt das Nachladen der Datenbasis.
         * Sitzungen mit `geklickt`, aber ohne `geoeffnet`, sind genau die, bei
         * denen es schiefging — ohne diese Trennung wäre der Ausfall im
         * Backend nicht von Desinteresse zu unterscheiden.
         */
        teaser_geklickt: 'geklickt',
        teaser_ladefehler: null,
        pdp_geoeffnet: 'geoeffnet',
        /* Der Kunde erklärt, dass er ein Gerät hat, und geht in die Suche. Das
         * ist eine Absicht, noch keine Angabe — die Leiter hat dafür keine
         * Sprosse, und eine zu erfinden hieße, Abbrüche vor der Gerätewahl als
         * Fortschritt zu zählen. Als Ereignis wird es trotzdem gezählt. */
        geraetesuche_geoeffnet: null,
        geraet_gewaehlt: 'geraet_genannt',
        /* Klick auf einen Zubehör-Artikel im Block zum Zubehör. */
        zubehoer_geklickt: 'lead',
        urteil_angezeigt: 'urteil',
        groessen_geoeffnet: 'vertieft',
        erweitern_geoeffnet: 'vertieft',
        vergleich_geoeffnet: 'vertieft',
        /*
         * Die Vorschlagsliste ist seit 2026-08-15 der EINSTIEG des Dialogs und
         * kein angeforderter Seitenweg mehr: Wer die Prüfung ohne gemerktes
         * Gerät öffnet, sieht sie sofort. Deshalb steht sie auf „geoeffnet" und
         * nicht mehr auf „vertieft" — sonst erreichte jede geöffnete Prüfung
         * automatisch die vorletzte Stufe, und die Tiefe sagte nichts mehr aus.
         *
         * Die beiden Namen bleiben getrennt: „Speicherseite ohne
         * Wechselrichter" und „Wechselrichter-Seite ohne Speicher" sind
         * verschiedene Kunden mit verschiedener Kaufabsicht, zusammengelegt
         * wäre in der Auswertung nicht mehr zu sehen, welcher Weg trägt.
         */
        wr_vorschlag_geoeffnet: 'geoeffnet',
        speicher_vorschlag_geoeffnet: 'geoeffnet',
        /* Eine andere Größe DERSELBEN Seite prüfen. Kein Fortschritt auf der
         * Leiter: Der Kunde stellt dieselbe Frage noch einmal, nur zu einer
         * anderen Größe — die Tiefe ändert das nicht. */
        groesse_gewaehlt: null,
        alternative_geklickt: 'lead',
        erweiterung_geklickt: 'lead',
        wr_vorschlag_geklickt: 'lead',
        speicher_vorschlag_geklickt: 'lead',
        variante_uebernommen: 'lead',
        berater_geoeffnet: 'lead',
        anfrage_geoeffnet: 'lead',
        /* Teaser eines Geräts ohne Batterieanschluss (seit 2026-09-26): „Sie
         * möchten einen Speicher?" aufgeklappt. Das ist dort der Einstieg in die
         * Wege — dieselbe Stufe wie ein geöffneter Dialog. */
        netz_wege_geoeffnet: 'geoeffnet',
        pdp_geschlossen: null,
        geraet_vergessen: null,
      },
      /*
       * `iv_handle` steht bewusst dabei, obwohl der Handle auch in der URL des
       * Seitenaufrufs steht: Eine Clarity-Sitzung umfasst mehrere Seiten. Ein
       * Filter auf die URL trifft die Sitzung, nicht den Teaser — nach drei
       * besuchten Produktseiten bliebe offen, auf welcher davon er benutzt
       * wurde. Der Tag sammelt genau die Produkte mit Teaser-Kontakt.
       */
      tags: ['iv_geraet', 'iv_befund', 'iv_ziel', 'iv_handle', 'iv_richtung'],
    },
    /*
     * Die Einstiege außerhalb von Berater und Produktseite (seit 2026-10-03,
     * app/berater-einstieg.js): Kategorie-Streifen, Startseite, Box und
     * Textlink in Artikeln und Themenseiten, Hauptmenü, Footer.
     */
    einstieg: {
      tiefe_schluessel: 'iv_einstieg_tiefe',
      leiter: ['gesehen', 'geklickt'],
      stufen: {
        gesehen: 'gesehen',
        geklickt: 'geklickt',
      },
      /*
       * Die Fläche steht in ZWEI Tags, je Stufe einer. Eine Sitzung sieht oft
       * mehrere Flächen (den Streifen auf der Kategorie, die Box im Artikel)
       * und klickt eine davon. Ein gemeinsamer Tag hielte beide Werte, und der
       * Klick ließe sich keiner Fläche mehr zuordnen. Getrennt ist die
       * Startquote einer Fläche eine einfache Division:
       * iv_flaeche_geklickt = x geteilt durch iv_flaeche_gesehen = x.
       */
      tags: ['iv_flaeche_gesehen', 'iv_flaeche_geklickt'],
    },
  };

  /* Clarity kürzt lange Tag-Werte. Wir kürzen selbst — dann steht im Backend,
   * was wir erwarten, und nicht, was die Grenze übrig gelassen hat. */
  var WERT_GRENZE = 100;

  /* Wie viele verschiedene Werte je Schlüssel gemerkt werden. Schutz gegen
   * einen Fehler an anderer Stelle, der in einer Schleife Tags setzt. */
  var MAX_WERTE = 12;

  /* Der Wachposten läuft nur, solange eine Einwilligung plausibel noch kommt.
   * 30 × 2 s: Wer nach einer Minute nicht geklickt hat, klickt auf dieser Seite
   * nicht mehr — ein Timer, der die ganze Sitzung läuft, wäre reine Last. */
  var TAKT_MS = 2000;
  var MAX_VERSUCHE = 30;

  var gemerkt = {};        // Schlüssel → alle bekannten Werte (gegen Wiederholung)
  var offen = [];          // [Schlüssel, Wert] — gemerkt, aber mangels Clarity ungesendet
  var hoechsteStufe = {};  // Kontext → Index in der Leiter
  var uhr = null;
  var versuche = 0;

  function verfuegbar() {
    return !!root && typeof root.clarity === 'function';
  }

  /* Eine Messung darf die Seite nie brechen — dieselbe Regel wie in emit(). */
  function sende(art, a, b) {
    if (!verfuegbar()) return false;
    try {
      root.clarity(art, a, b);
      return true;
    } catch (fehler) {
      return false;
    }
  }

  /*
   * Warten, bis Clarity nach erteilter Einwilligung erscheint — und dann die
   * Tags nachziehen. Nur die Tags: Die Ereignisse aus der Zeit davor sind
   * bewusst verloren (siehe Kopf der Datei).
   */
  function warte() {
    if (uhr || !root || typeof root.setInterval !== 'function') return;
    versuche = 0;
    uhr = root.setInterval(function () {
      versuche++;
      if (verfuegbar()) {
        stoppe();
        nachziehen();
      } else if (versuche >= MAX_VERSUCHE) {
        stoppe();
      }
    }, TAKT_MS);
  }

  function stoppe() {
    if (!uhr) return;
    root.clearInterval(uhr);
    uhr = null;
  }

  /*
   * Nachgezogen wird nur, was mangels Clarity offengeblieben ist — nicht der
   * ganze bekannte Bestand. Sonst käme jeder Wert, der zwischen Einwilligung
   * und dem nächsten Takt des Wachpostens bereits durchging, ein zweites Mal.
   */
  function nachziehen() {
    var liste = offen;
    offen = [];
    for (var i = 0; i < liste.length; i++) {
      if (!sende('set', liste[i][0], liste[i][1])) offen.push(liste[i]);
    }
  }

  function normalisiere(wert) {
    if (wert === null || wert === undefined || wert === '') return null;
    if (typeof wert === 'boolean') return wert ? 'ja' : 'nein';
    var text = String(wert);
    if (!text || text === 'null' || text === 'undefined') return null;
    return text.length > WERT_GRENZE ? text.slice(0, WERT_GRENZE) : text;
  }

  /*
   * Ein Tag setzen. Jeder Wert genau einmal — ein Schlüssel kann dabei mehrere
   * Werte tragen (die Stufenleiter macht davon Gebrauch).
   */
  function tag(schluessel, wert) {
    var text = normalisiere(wert);
    if (!schluessel || text === null) return;

    var werte = gemerkt[schluessel] || (gemerkt[schluessel] = []);
    for (var i = 0; i < werte.length; i++) {
      if (werte[i] === text) return;
    }
    if (werte.length >= MAX_WERTE) return;
    werte.push(text);

    /* Nicht gesendet heißt: noch keine Einwilligung. Der Wert bleibt offen und
     * geht mit, sobald Clarity da ist. */
    if (!sende('set', schluessel, text)) {
      offen.push([schluessel, text]);
      warte();
      return;
    }

    /*
     * Ging der Wert durch, ist Clarity da — dann gehört alles mit, was noch
     * offensteht. Ohne diese Zeile bliebe es liegen, sobald der Wachposten
     * bereits aufgegeben hat: Er ist die einzige andere Stelle, die nachzieht,
     * und er läuft nur eine Minute.
     *
     * Aufgefallen am 2026-08-14 an den echten Zahlen im Backend: Der Teaser
     * zählte 9 Sitzungen mit `geklickt`, aber 10 mit `geoeffnet` — obwohl das
     * Overlay nur über den Klick zu erreichen ist. Genau eine Sitzung hatte
     * spät eingewilligt: Das Overlay meldete durch, die Stufen davor blieben
     * für immer liegen und die Klickrate wäre zu niedrig ausgefallen.
     */
    if (offen.length) nachziehen();
  }

  function tiefe(kontextName, kontext, stufe) {
    var index = kontext.leiter.indexOf(stufe);
    if (index === -1) return;
    var bisher = hoechsteStufe[kontextName];
    if (bisher !== undefined && index <= bisher) return;
    hoechsteStufe[kontextName] = index;
    tag(kontext.tiefe_schluessel, stufe);
  }

  /*
   * Der Ereignisname im Backend. Präfix `iv_`, damit unsere Ereignisse in der
   * Clarity-Liste beieinanderstehen, und der Kontext im Namen, weil
   * `vergleich_geoeffnet` auf der Beraterseite etwas anderes meint als auf der
   * Produktseite. Namen, die schon mit dem Kontext beginnen (`pdp_geoeffnet`),
   * bekommen ihn nicht doppelt.
   */
  function ereignisName(kontextName, ivEvent) {
    return ivEvent.indexOf(kontextName + '_') === 0
      ? 'iv_' + ivEvent
      : 'iv_' + kontextName + '_' + ivEvent;
  }

  /*
   * Die einzige Stelle, die von außen gerufen wird — aus emit() beider Module,
   * aus dem Lader des Produktseiten-Snippets und aus berater-einstieg.js.
   */
  function melde(kontextName, ivEvent, daten) {
    var kontext = KONTEXTE[kontextName];
    if (!kontext || !ivEvent) return;

    tag('iv_kontext', kontextName);

    var stufe = Object.prototype.hasOwnProperty.call(kontext.stufen, ivEvent)
      ? kontext.stufen[ivEvent] : null;
    if (stufe) tiefe(kontextName, kontext, stufe);

    if (daten) {
      for (var i = 0; i < kontext.tags.length; i++) {
        var feld = kontext.tags[i];
        if (Object.prototype.hasOwnProperty.call(daten, feld)) tag(feld, daten[feld]);
      }
    }

    if (!sende('event', ereignisName(kontextName, ivEvent))) warte();
    else if (offen.length) nachziehen();
  }

  return {
    melde: melde,
    tag: tag,
    verfuegbar: verfuegbar,
    KONTEXTE: KONTEXTE,
    /* Für Tests und für die Fehlersuche im Browser: was ist bisher gesetzt? */
    zustand: function () {
      return { tags: gemerkt, offen: offen.length, tiefe: hoechsteStufe, wartet: !!uhr };
    },
  };
});
